import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type CardStyleSettings,
  type ChampionsCardContent,
} from "./types";

interface ChampionsCardProps {
  format: CardFormat;
  content: ChampionsCardContent;
}

const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";

/**
 * Championship celebration poster. The entire palette is driven off the
 * winning nation's three colours, so flipping the winner re-skins the card
 * without touching layout — which is the point: this gets built before the
 * final and published seconds after it.
 */
const ChampionsCard = forwardRef<HTMLDivElement, ChampionsCardProps>(
  function ChampionsCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const isPortrait = content.variant === "portrait";

    const { primaryColor: primary, accentColor: accent } = content;

    // Glory: nation colour radiates from a bright centre out to near-black
    // at the corners, so the gold wordmark always has contrast. Portrait:
    // near-black base since the photo carries the frame.
    const baseGradient = isPortrait
      ? "linear-gradient(180deg, #050505 0%, #000000 100%)"
      : [
          `radial-gradient(ellipse 85% 55% at 50% 38%, ${primary}FF 0%, ${primary}CC 32%, transparent 72%)`,
          `radial-gradient(ellipse 60% 40% at 50% 34%, ${accent}55 0%, transparent 65%)`,
          "linear-gradient(180deg, #0A0A0A 0%, #050505 55%, #000000 100%)",
        ].join(", ");

    const shellBackground =
      isPortrait && content.heroImageUrl
        ? {
            imageUrl: content.heroImageUrl,
            opacity: content.heroImageOpacity ?? 0.85,
          }
        : style.background;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("CHAMPIONS")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={shellBackground}
        customBaseGradient={baseGradient}
        suppressBrandOverlays
        hideFooter={content.footer?.hide}
      >
        {/* Light rays fanning from behind the wordmark — the "stadium
            floodlight" moment. Glory only; on portrait the photo owns the
            frame and rays would fight it. */}
        {!isPortrait && <GloryRays accent={accent} />}

        {/* Portrait needs the same bottom-vignette treatment Factsheet uses
            so the lockup reads over any photo. Inset -800 clears the shell
            padding + header/footer siblings; overflow:hidden clips it. */}
        {isPortrait && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -800,
              background: `linear-gradient(180deg,
                rgba(0,0,0,0.55) 0%,
                transparent 28%,
                transparent 46%,
                rgba(0,0,0,0.72) 74%,
                rgba(0,0,0,0.94) 100%)`,
              pointerEvents: "none",
            }}
          />
        )}

        <div
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: isPortrait ? "flex-end" : "center",
            minHeight: 0,
            paddingBottom: isPortrait ? (isStory ? 40 : 24) : 0,
          }}
        >
          <ChampionsLockup
            content={content}
            format={format}
            style={style}
            compact={isPortrait}
          />
        </div>
      </FbkCardShell>
    );
  },
);

export default ChampionsCard;

// ─── Shared lockup ─────────────────────────────────────────────────────────
// Eyebrow → trophy (glory only) → flag + nation → CHAMPIONS → stars →
// title count → score → message → date. Ordering is deliberate: the two
// things a scrolling viewer must catch in half a second are the flag and
// the word CHAMPIONS, so they sit dead centre with the most weight.

function ChampionsLockup({
  content,
  format,
  style,
  compact,
}: {
  content: ChampionsCardContent;
  format: CardFormat;
  style: CardStyleSettings;
  /** Portrait variant packs tighter — the photo needs the room. */
  compact: boolean;
}) {
  const isStory = format === "story";
  const accent = content.accentColor;

  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 24,
        color: "rgba(255,255,255,0.92)",
        letterSpacing: "0.5em",
        bold: true,
        align: "center",
      },
      format,
    ),
    textTransform: "uppercase",
    textShadow: "0 2px 10px rgba(0,0,0,0.8)",
  };

  // The hero word. Gold foil gradient + heavy glow — this is the single
  // element the whole card is built around.
  const championStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: compact ? 200 : 260,
        color: accent,
        letterSpacing: "0.01em",
        bold: true,
        align: "center",
      },
      format,
    ),
    lineHeight: 0.88,
    textTransform: "uppercase",
    background: `linear-gradient(180deg, #FFFFFF 0%, ${accent} 42%, ${accent} 62%, #B8860B 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    filter: `drop-shadow(0 0 60px ${accent}66) drop-shadow(0 8px 24px rgba(0,0,0,0.85))`,
  };

  const nationStyle: CSSProperties = {
    ...resolveTextStyle(
      style.tagline,
      {
        fontFamily: FONT_BEBAS,
        fontSizePx: compact ? 78 : 96,
        color: "#FFFFFF",
        letterSpacing: "0.14em",
        bold: true,
        align: "center",
      },
      format,
    ),
    lineHeight: 1,
    textTransform: "uppercase",
    textShadow: "0 4px 20px rgba(0,0,0,0.85), 0 2px 4px rgba(0,0,0,0.95)",
  };

  const messageStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: FONT_INTER,
        fontSizePx: 28,
        color: "rgba(255,255,255,0.92)",
        bold: false,
        align: "center",
      },
      format,
    ),
    lineHeight: 1.45,
    maxWidth: isStory ? 840 : 700,
    textShadow: "0 2px 12px rgba(0,0,0,0.85)",
  };

  const gap = (story: number, post: number) => (isStory ? story : post);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {content.eyebrow && (
        <div style={{ ...eyebrowStyle, marginBottom: gap(28, 18) }}>
          {content.eyebrow}
        </div>
      )}

      {/* Trophy — glory variant only. On portrait the celebration photo IS
          the imagery, so a second trophy would double up. */}
      {!compact && content.heroImageUrl && (
        <img
          src={content.heroImageUrl}
          alt=""
          crossOrigin={
            content.heroImageUrl.startsWith("data:") ||
            content.heroImageUrl.startsWith("/")
              ? undefined
              : "anonymous"
          }
          style={{
            height: gap(360, 250),
            width: "auto",
            objectFit: "contain",
            marginBottom: gap(24, 16),
            filter: `drop-shadow(0 0 70px ${accent}88) drop-shadow(0 20px 40px rgba(0,0,0,0.7))`,
          }}
        />
      )}

      {/* Flag + nation name on one line */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: gap(26, 18),
          marginBottom: gap(10, 6),
        }}
      >
        {content.flagUrl && (
          <img
            src={content.flagUrl}
            alt=""
            crossOrigin={
              content.flagUrl.startsWith("data:") ? undefined : "anonymous"
            }
            style={{
              width: gap(132, 96),
              height: gap(92, 67),
              objectFit: "cover",
              borderRadius: 10,
              border: `3px solid ${accent}`,
              boxShadow: `0 0 40px ${accent}55, 0 10px 26px rgba(0,0,0,0.7)`,
            }}
          />
        )}
        <div style={nationStyle}>{content.nationName}</div>
      </div>

      <div style={championStyle}>{content.championLabel}</div>

      {/* Star row — one per world title held after this win */}
      {!!content.starCount && content.starCount > 0 && (
        <div
          style={{
            display: "flex",
            gap: gap(14, 10),
            marginTop: gap(20, 14),
          }}
        >
          {Array.from({ length: Math.min(content.starCount, 8) }, (_, i) => (
            <span
              key={i}
              style={{
                fontSize: gap(46, 34),
                lineHeight: 1,
                color: accent,
                filter: `drop-shadow(0 0 18px ${accent}AA) drop-shadow(0 3px 6px rgba(0,0,0,0.8))`,
              }}
            >
              ★
            </span>
          ))}
        </div>
      )}

      {content.titleCountLabel && (
        <div
          style={{
            marginTop: gap(14, 10),
            fontFamily: FONT_INTER,
            fontSize: gap(24, 18),
            fontWeight: 800,
            color: accent,
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            textAlign: "center",
            textShadow: "0 2px 10px rgba(0,0,0,0.85)",
          }}
        >
          {content.titleCountLabel}
        </div>
      )}

      {/* Score — boxed so it reads as a result, not a caption */}
      {content.scoreLine && (
        <div
          style={{
            marginTop: gap(30, 20),
            padding: `${gap(14, 10)}px ${gap(34, 24)}px`,
            borderRadius: 9999,
            background: "rgba(0,0,0,0.55)",
            border: `1px solid ${accent}66`,
            fontFamily: FONT_BEBAS,
            fontSize: gap(46, 34),
            color: "#FFFFFF",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
          }}
        >
          {content.scoreLine}
        </div>
      )}

      {content.message && (
        <div style={{ ...messageStyle, marginTop: gap(26, 18) }}>
          {content.message}
        </div>
      )}

      {content.dateLine && (
        <div
          style={{
            marginTop: gap(22, 15),
            fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
            fontSize: gap(20, 15),
            fontWeight: 700,
            color: "rgba(255,255,255,0.78)",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          {content.dateLine}
        </div>
      )}
    </div>
  );
}

// ─── Glory backdrop ────────────────────────────────────────────────────────

/**
 * Twelve light rays fanning from a point behind the wordmark, plus a bright
 * core. Pure CSS conic-free implementation (rotated gradient slivers) so it
 * rasterises reliably through html-to-image.
 */
function GloryRays({ accent }: { accent: string }) {
  const rayCount = 12;
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: -800,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 2400,
          height: 2400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {Array.from({ length: rayCount }, (_, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 120,
              height: 2400,
              background: `linear-gradient(180deg, transparent 0%, ${accent}22 38%, ${accent}33 50%, ${accent}22 62%, transparent 100%)`,
              transform: `rotate(${(180 / rayCount) * i}deg)`,
              transformOrigin: "center center",
              filter: "blur(6px)",
            }}
          />
        ))}
        {/* Bright core so the rays read as emanating, not just crossing */}
        <div
          style={{
            position: "absolute",
            width: 900,
            height: 900,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accent}44 0%, ${accent}18 38%, transparent 70%)`,
            filter: "blur(10px)",
          }}
        />
      </div>
    </div>
  );
}
